import { ComponentStyleProps } from "../../../lib/styles/props/component-style-props";
import DatePicker from "react-datepicker";
import { CMCustomDatePickerInput } from "../../control";
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  getDateYmd,
  ymdTextToDateOrNull,
  ymToDate,
  ymToText,
} from "../../../lib/util/common-date-util";
import { hasValue, isNullish } from "../../../lib/util/common-util";
import { FiCalendar } from "react-icons/fi";
import { ComponentSingleStyleConfig, useStyleConfig } from "@chakra-ui/react";
import { SystemStyleObject } from "@chakra-ui/styled-system";

export type CMYearMonthPickerProps = {
  size?: "sm" | "md" | "lg";

  placeholder?: string;
  icon?: ReactElement;

  selected?: { year: number; month: number } | null;

  disabled?: boolean;
  isInvalid?: boolean;

  minDate?: {
    year: number;
    month: number;
  };
  maxDate?: {
    year: number;
    month: number;
  };

  /**
   * 日付の表示形式。
   */
  dateFormat?: string;
  /**
   * 日付の区切り文字。
   * 日付の表示形式で使用している区切り文字と同じにすること。
   */
  separator?: string;

  onChange?: (value: { year: number; month: number } | null) => void;
  onBlur?: () => void;

  /**
   * 入力エリアのスタイル。
   */
  inputStyles?: SystemStyleObject;
} & ComponentStyleProps;
export const CMYearMonthPicker = ({
  size,

  placeholder,
  icon = <FiCalendar />,

  selected = null,

  disabled,
  isInvalid,

  minDate,
  maxDate,

  dateFormat,
  separator,

  onChange,
  onBlur,

  inputStyles,

  sx,
  ...rest
}: CMYearMonthPickerProps) => {
  const styles = useStyleConfig("CMYearMonthPickerStyles");

  const _selected = useMemo(
    () => (hasValue(selected) ? ymToDate(selected) : null),
    [selected],
  );
  const selectedText = useMemo(
    () => (hasValue(selected) ? ymToText(selected) : ""),
    [selected],
  );
  const [textValue, setTextValue] = useState<string>(selectedText);

  useEffect(() => {
    setTextValue(selectedText);
  }, [selectedText]);

  const minDateObject = useMemo(
    () => (hasValue(minDate) ? ymToDate(minDate) : null),
    [minDate],
  );
  const maxDateObject = useMemo(
    () => (hasValue(maxDate) ? ymToDate(maxDate) : null),
    [maxDate],
  );

  const handleChange = useCallback(
    (value: Date | null) => {
      if (isNullish(value)) {
        onChange?.(null);
        return;
      }
      const [year, month] = getDateYmd(value);
      onChange?.({ year, month });
    },
    [onChange],
  );
  const handleTextInput = useCallback(
    (value: string) => {
      setTextValue(value);

      // 空文字列なら入力値のクリアと見做し、nullを返す。
      if (value === "") {
        handleChange(null);
        return;
      }

      // 日付形式として正しければDateオブジェクトを作成し、onChangeで返す。
      const dateObject = ymdTextToDateOrNull(value, separator);
      if (hasValue(dateObject)) handleChange(dateObject);
    },
    [handleChange, separator],
  );

  // カレンダーのふきだし位置が崩れているので固定する
  // https://github.com/Hacker0x01/react-datepicker/issues/3176
  const popperModifiers = useMemo(
    () => [
      {
        name: "arrow",
        options: {
          padding: ({ popper }: any) => ({
            right: popper.width - 32,
          }),
        },
      },
    ],
    [],
  );

  return (
    <DatePicker
      showMonthYearPicker
      selected={_selected}
      value={textValue}
      onChange={handleChange}
      dateFormat={dateFormat}
      placeholderText={placeholder}
      minDate={minDateObject}
      maxDate={maxDateObject}
      popperModifiers={popperModifiers}
      customInput={
        <CMCustomDatePickerInput
          size={size}
          disabled_={disabled}
          isInvalid={isInvalid}
          icon={icon}
          onInput={handleTextInput}
          onBlur={onBlur}
          inputStyles={inputStyles}
          sx={{ ...styles, ...sx }}
          {...rest}
        />
      }
    />
  );
};
export const CMYearMonthPickerStyles: ComponentSingleStyleConfig = {
  baseStyle: {},
};
